import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success('If that email exists, a reset link has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Login
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
            alt="2nd Team Casting" 
            className="h-36 w-auto"
          />
        </div>

        {!sent ? (
          <>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
              Forgot Password
            </h1>
            <p className="text-white/60 mb-8">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30 focus:border-[#D4AF37] h-12"
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full gold-gradient text-black font-bold h-12 uppercase tracking-wider hover:brightness-110 transition-all disabled:opacity-50"
                data-testid="forgot-submit-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-4">
              Check Your Email
            </h1>
            <p className="text-white/60 mb-8">
              If an account with <span className="text-white">{email}</span> exists, we've sent a password reset link. Check your inbox (and spam folder).
            </p>
            <Link to="/login">
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37]" data-testid="back-to-login-btn">
                Back to Login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
